import { RetourChantier, RetourChantierItem, StockMovement } from '../types';
import { productService } from './productService';
import { chantierService } from './chantierService';

// Mock data
const mockRetours: RetourChantier[] = [
  {
    id: 'retour-1',
    retourNumber: 'RET-2024-001',
    chantierId: 'chantier-1',
    status: 'en_attente',
    items: [
      {
        productId: 'prod-1',
        quantity: 25,
        unitPrice: 0.15,
        totalValue: 3.75,
        condition: 'bon',
        comment: 'Vis non utilisées'
      },
      {
        productId: 'prod-2',
        quantity: 3,
        unitPrice: 25.50,
        totalValue: 76.50,
        condition: 'bon',
        comment: 'Planches en surplus'
      }
    ],
    totalValue: 80.25,
    reason: 'Surplus de matériaux après fin des travaux',
    createdBy: '3',
    createdAt: '2024-12-16T14:30:00Z',
    updatedAt: '2024-12-16T14:30:00Z'
  },
  {
    id: 'retour-2',
    retourNumber: 'RET-2024-002',
    chantierId: 'chantier-2',
    status: 'valide',
    items: [
      {
        productId: 'prod-3',
        quantity: 2,
        unitPrice: 8.50,
        totalValue: 17.00,
        condition: 'utilisable',
        comment: 'Tubes de colle entamés mais utilisables'
      }
    ],
    totalValue: 17.00,
    reason: 'Changement de spécifications client',
    createdBy: '4',
    validatedBy: '2',
    validatedAt: '2024-12-15T16:00:00Z',
    createdAt: '2024-12-15T11:00:00Z',
    updatedAt: '2024-12-15T16:00:00Z'
  }
];

class RetourService {
  async getRetours(filters?: {
    search?: string;
    status?: string;
    chantierId?: string;
  }): Promise<RetourChantier[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    
    let filtered = [...mockRetours];
    
    if (filters?.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(r => 
        r.retourNumber.toLowerCase().includes(search) ||
        r.reason?.toLowerCase().includes(search)
      );
    }
    
    if (filters?.status) {
      filtered = filtered.filter(r => r.status === filters.status);
    }
    
    if (filters?.chantierId) {
      filtered = filtered.filter(r => r.chantierId === filters.chantierId);
    }
    
    return filtered.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  async getRetour(id: string): Promise<RetourChantier | null> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockRetours.find(r => r.id === id) || null;
  }

  async getRetoursByChantier(chantierId: string): Promise<RetourChantier[]> {
    await new Promise(resolve => setTimeout(resolve, 200));
    return mockRetours.filter(r => r.chantierId === chantierId);
  }

  async createRetour(retour: Omit<RetourChantier, 'id' | 'retourNumber' | 'createdAt' | 'updatedAt'>): Promise<RetourChantier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const retourNumber = `RET-${new Date().getFullYear()}-${String(mockRetours.length + 1).padStart(3, '0')}`;
    
    const newRetour: RetourChantier = {
      ...retour,
      id: `retour-${Date.now()}`,
      retourNumber,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    mockRetours.push(newRetour);
    return newRetour;
  }

  async updateRetour(id: string, updates: Partial<RetourChantier>): Promise<RetourChantier> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const index = mockRetours.findIndex(r => r.id === id);
    if (index === -1) throw new Error('Retour non trouvé');
    
    mockRetours[index] = {
      ...mockRetours[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };
    
    return mockRetours[index];
  }

  async validateRetour(retourId: string, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const retour = mockRetours.find(r => r.id === retourId);
    if (!retour) throw new Error('Retour non trouvé');
    
    if (retour.status !== 'en_attente') {
      throw new Error('Ce retour ne peut pas être validé');
    }
    
    // Générer les mouvements de stock pour les articles en bon état ou utilisables
    for (const item of retour.items) {
      if (item.condition === 'bon' || item.condition === 'utilisable') {
        const movement: StockMovement = {
          id: `mov-retour-${Date.now()}-${item.productId}`,
          productId: item.productId,
          type: 'entry',
          quantity: item.quantity,
          reason: `Retour chantier ${retour.retourNumber}`,
          userId,
          createdAt: new Date().toISOString()
        };
        
        // En réalité, cela mettrait à jour le stock du produit
        // await productService.adjustStock(item.productId, item.quantity, movement.reason, userId);
      }
    }
    
    // Mettre à jour le coût du chantier
    await this.updateChantierCost(retour.chantierId, -retour.totalValue);
    
    retour.status = 'valide';
    retour.validatedBy = userId;
    retour.validatedAt = new Date().toISOString();
    retour.updatedAt = new Date().toISOString();
  }

  async rejectRetour(retourId: string, userId: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const retour = mockRetours.find(r => r.id === retourId);
    if (!retour) throw new Error('Retour non trouvé');
    
    if (retour.status !== 'en_attente') {
      throw new Error('Ce retour ne peut pas être refusé');
    }
    
    retour.status = 'refuse';
    retour.validatedBy = userId;
    retour.validatedAt = new Date().toISOString();
    retour.updatedAt = new Date().toISOString();
  }

  private async updateChantierCost(chantierId: string, adjustment: number): Promise<void> {
    // En réalité, cela mettrait à jour le coût total du chantier
    // await chantierService.adjustCost(chantierId, adjustment);
  }

  async getChantierRetoursSummary(chantierId: string): Promise<{
    totalRetours: number;
    totalValue: number;
    validatedRetours: number;
    validatedValue: number;
  }> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const retours = mockRetours.filter(r => r.chantierId === chantierId);
    const validatedRetours = retours.filter(r => r.status === 'valide');
    
    return {
      totalRetours: retours.length,
      totalValue: retours.reduce((sum, r) => sum + r.totalValue, 0),
      validatedRetours: validatedRetours.length,
      validatedValue: validatedRetours.reduce((sum, r) => sum + r.totalValue, 0)
    };
  }

  // Helper methods
  getStatusLabel(status: string): string {
    const labels: Record<string, string> = {
      en_attente: 'En attente',
      valide: 'Validé',
      refuse: 'Refusé'
    };
    return labels[status] || status;
  }

  getStatusColor(status: string): string {
    const colors: Record<string, string> = {
      en_attente: 'bg-orange-100 text-orange-800',
      valide: 'bg-green-100 text-green-800',
      refuse: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  getConditionLabel(condition: string): string {
    const labels: Record<string, string> = {
      bon: 'Bon état',
      utilisable: 'Utilisable',
      abime: 'Abîmé'
    };
    return labels[condition] || condition;
  }

  canUserManageRetours(userRole: string): boolean {
    return ['admin', 'responsable_depot', 'responsable_bureau'].includes(userRole);
  }

  canUserCreateRetours(userRole: string): boolean {
    return ['admin', 'responsable_depot', 'responsable_bureau', 'responsable_equipe'].includes(userRole);
  }
}

export const retourService = new RetourService();